import { useEffect } from 'react';
import { NavLink } from 'react-router';
import {
  FiHome,
  FiUsers,
  FiActivity,
  FiCalendar,
  FiChevronLeft,
  FiChevronRight,
  FiGrid,
  FiMapPin,
} from 'react-icons/fi';
import { MdDirectionsBike, MdAdminPanelSettings, MdPerson, MdInventory2, MdWorkspacePremium } from 'react-icons/md';
import type { IconType } from 'react-icons';
import clsx from 'clsx';
import { useAuth } from '@/contexts/AuthContext';
import { atalhosVisiveis } from '@/lib/atalhosOutros';

/** Grupos do Cognito que decidem a visibilidade de um item de navegação. */
type NavGroup = 'admin' | 'clubes' | 'ciclista';

interface NavItemDef {
  path: string;
  label: string;
  icon: IconType;
  /** Grupos que enxergam o item. Vazio nunca — um item sem grupo não aparece para ninguém. */
  groups: NavGroup[];
  /**
   * Grupos que, se presentes, escondem o item mesmo estando em `groups` — hoje só
   * "Meu Perfil", que é a entrada do ciclista puro e some para quem também
   * administra.
   */
  hiddenFor?: NavGroup[];
}

/**
 * Fonte declarativa única da navegação.
 *
 * A visibilidade de cada entrada tem que corresponder ao guard da rota em
 * `App.tsx`: um destino que o guard admite e o menu esconde vira rota alcançável
 * só por URL; o inverso é item que leva a um 'acesso negado'. A referência de
 * quem vê o quê é `docs/business-rules.md` § Controle de Acesso por Perfil —
 * grupo Clubes tem acesso apenas a "Meu Clube" e aos brevets do próprio clube.
 *
 * Declarar em lista (em vez de um bloco condicional por item) é o que garante a
 * deduplicação por `path` abaixo: "Brevets" é visível para dois grupos e
 * aparecia duas vezes para quem estivesse nos dois.
 */
const NAV_ITEMS: NavItemDef[] = [
  { path: '/dashboard', label: 'Dashboard', icon: FiHome, groups: ['admin', 'clubes', 'ciclista'] },
  { path: '/clube', label: 'Meu Clube', icon: FiUsers, groups: ['clubes'] },
  { path: '/brevets', label: 'Brevets', icon: FiActivity, groups: ['admin', 'clubes'] },
  { path: '/calendario', label: 'Calendário', icon: FiCalendar, groups: ['admin', 'clubes'] },
  { path: '/clubes', label: 'Clubes', icon: FiUsers, groups: ['admin'] },
  { path: '/mapa-clubes', label: 'Mapa de Clubes', icon: FiMapPin, groups: ['admin'] },
  { path: '/ciclistas', label: 'Ciclistas', icon: MdPerson, groups: ['admin'] },
  { path: '/randonneurs', label: 'Randonneurs', icon: MdDirectionsBike, groups: ['admin'] },
  { path: '/condecorados', label: 'Condecorados', icon: MdWorkspacePremium, groups: ['admin', 'clubes'] },
  { path: '/medalhas-estoque', label: 'Estoque de Medalhas', icon: MdInventory2, groups: ['admin', 'clubes'] },
  { path: '/usuarios', label: 'Usuários', icon: MdAdminPanelSettings, groups: ['admin'] },
  { path: '/meu-perfil', label: 'Meu Perfil', icon: MdPerson, groups: ['ciclista'], hiddenFor: ['admin', 'clubes'] },
  // Último de propósito: não é uma feature, é a porta para as telas secundárias.
  // Os grupos aqui são a união dos grupos dos atalhos; quem não enxerga nenhum
  // atalho perde o item, pelo filtro em `visibleNavItems`. Com o catálogo vazio
  // (`ATALHOS_OUTROS`), hoje ele não aparece para ninguém.
  { path: '/outros', label: 'Outros', icon: FiGrid, groups: ['admin'] },
];

/**
 * Itens visíveis para os grupos do usuário, sem destino repetido — um usuário em
 * Admins **e** Clubes vê "Brevets" uma única vez, na ordem de declaração.
 */
function visibleNavItems(groups: NavGroup[]): NavItemDef[] {
  const seen = new Set<string>();
  return NAV_ITEMS.filter((item) => {
    if (item.hiddenFor?.some((g) => groups.includes(g))) return false;
    if (!item.groups.some((g) => groups.includes(g))) return false;
    // "Outros" leva a um hub cujo conteúdo depende do perfil. Exibi-lo para quem
    // não enxerga nenhum atalho seria conduzir o usuário a uma tela vazia.
    if (item.path === '/outros' && atalhosVisiveis(groups).length === 0) return false;
    if (seen.has(item.path)) return false;
    seen.add(item.path);
    return true;
  });
}

interface Props {
  collapsed: boolean;
  onToggleCollapse: () => void;
  mobileOpen: boolean;
  onCloseMobile: () => void;
}

export default function Sidebar({ collapsed, onToggleCollapse, mobileOpen, onCloseMobile }: Readonly<Props>) {
  const { isAdmin, isClubes, isCiclista, user } = useAuth();

  const groups: NavGroup[] = [
    ...(isAdmin() ? (['admin'] as const) : []),
    ...(isClubes() ? (['clubes'] as const) : []),
    ...(isCiclista() ? (['ciclista'] as const) : []),
  ];
  const items = visibleNavItems(groups);

  useEffect(() => {
    if (!mobileOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseMobile();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileOpen, onCloseMobile]);

  return (
    <>
      {mobileOpen && (
        <div
          data-testid="mobile-sidebar-backdrop"
          className="fixed inset-0 z-30 bg-black/50 md:hidden"
          aria-hidden="true"
          onClick={onCloseMobile}
        />
      )}

      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-40 flex w-64 flex-col bg-shell',
          'transition-all duration-200 ease-in-out motion-reduce:transition-none',
          'md:static md:z-auto md:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
          collapsed ? 'md:w-16' : 'md:w-64',
        )}
      >
        <nav className="flex-1 px-4 pb-4 space-y-1 pt-4">
          {items.map(({ path, label, icon: Icon }) => (
            <NavItem
              key={path}
              path={path}
              label={label}
              icon={<Icon size={18} />}
              collapsed={collapsed}
              onNavigate={onCloseMobile}
            />
          ))}
        </nav>

        {user && (
          <div className={clsx('px-4 pb-4', collapsed && 'md:hidden')}>
            <div className="px-3 py-2 rounded-lg bg-shell-hover/50">
              <p className="text-shell-ink/70 text-xs">
                {user.groups.length > 0 ? user.groups.join(', ') : 'Sem grupo'}
              </p>
            </div>
          </div>
        )}

        <div className="hidden border-t border-shell-line p-2 md:block">
          <button
            type="button"
            onClick={onToggleCollapse}
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
            title={collapsed ? 'Expandir menu' : 'Recolher menu'}
            className="flex w-full items-center justify-center rounded-lg p-2 text-shell-ink transition-colors hover:bg-shell-hover hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            {collapsed ? <FiChevronRight size={18} /> : <FiChevronLeft size={18} />}
          </button>
        </div>
      </aside>
    </>
  );
}

function NavItem({
  path,
  label,
  icon,
  collapsed,
  onNavigate,
}: Readonly<{
  path: string;
  label: string;
  icon: React.ReactNode;
  collapsed: boolean;
  onNavigate: () => void;
}>) {
  return (
    <NavLink
      to={path}
      title={label}
      onClick={onNavigate}
      className={({ isActive }) =>
        clsx(
          'flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors',
          collapsed && 'md:justify-center md:px-0',
          isActive ? 'bg-shell-active text-white' : 'text-shell-ink hover:bg-shell-hover hover:text-white',
        )
      }
    >
      {icon}
      <span className={clsx(collapsed && 'md:sr-only')}>{label}</span>
    </NavLink>
  );
}
